/**
 * DEALER LOCATOR MODERN - JavaScript
 * Leaflet Map with Custom Excavator Markers
 */

(function() {
  'use strict';

  const config = window.SeKaDealerConfig || {};
  let map = null;
  let markers = [];
  let activeMarker = null;
  let activeCard = null;

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Initialize the Dealer Locator
   */
  function init() {
    // Handle intro animation
    if (config.enableIntro && !prefersReducedMotion) {
      runIntroAnimation();
    } else {
      // Remove overlay immediately if no animation
      const overlay = document.getElementById('dealer-intro-overlay');
      if (overlay) overlay.remove();
      initMap();
    }

    // Initialize UI
    initSearch();
    initMobileToggle();
    initCardInteractions();
    initRouteButtons();
    animateElements();
  }

  /**
   * Run the globe intro animation
   */
  function runIntroAnimation() {
    const overlay = document.getElementById('dealer-intro-overlay');
    if (!overlay) {
      initMap();
      return;
    }

    // Wait for animation to complete
    setTimeout(() => {
      overlay.classList.add('fade-out');
      initMap();
      
      setTimeout(() => {
        overlay.remove();
      }, 600);
    }, 2500);
  }

  /**
   * Initialize Leaflet Map
   */
  function initMap() {
    const mapContainer = document.getElementById('dealer-map');
    if (!mapContainer || typeof L === 'undefined') return;

    // Create map
    map = L.map('dealer-map', {
      center: [config.defaultLat, config.defaultLng],
      zoom: config.defaultZoom,
      zoomControl: true,
      scrollWheelZoom: true
    });

    // Add tile layer (OpenStreetMap with dark styling)
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19
    }).addTo(map);
    map.getPane('tilePane').style.filter = 'invert(1) hue-rotate(180deg) brightness(0.75) contrast(1.05) saturate(0.2) grayscale(0.25)';

    // Create custom excavator icon
    const excavatorIcon = L.divIcon({
      className: 'dealer-marker-wrapper',
      html: `<img src="${config.markerIcon}" class="dealer-marker" alt="Händler" style="width:40px;height:40px;" />`,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -40]
    });

    const excavatorIconFeatured = L.divIcon({
      className: 'dealer-marker-wrapper',
      html: `<img src="${config.markerIcon}" class="dealer-marker" alt="Premium Händler" style="width:48px;height:48px;filter:drop-shadow(0 0 8px rgba(248,212,0,0.6));" />`,
      iconSize: [48, 48],
      iconAnchor: [24, 48],
      popupAnchor: [0, -48]
    });

    // Add markers for each dealer
    config.dealers.forEach(dealer => {
      if (!dealer.lat || !dealer.lng) return;

      const icon = dealer.featured ? excavatorIconFeatured : excavatorIcon;
      
      const marker = L.marker([dealer.lat, dealer.lng], { icon: icon })
        .addTo(map)
        .bindPopup(createPopupContent(dealer), {
          className: 'dealer-popup-wrapper',
          closeButton: true,
          autoPan: true
        });

      marker.dealerId = dealer.id;

      // Marker events
      marker.on('click', () => {
        setActiveMarker(marker);
        scrollToCard(dealer.id);
        highlightCard(dealer.id);
      });

      marker.on('mouseover', () => {
        const el = marker.getElement();
        if (el) {
          const img = el.querySelector('.dealer-marker');
          if (img) img.style.transform = 'translateY(-4px) scale(1.1)';
        }
      });

      marker.on('mouseout', () => {
        const el = marker.getElement();
        if (el && marker !== activeMarker) {
          const img = el.querySelector('.dealer-marker');
          if (img) img.style.transform = '';
        }
      });

      markers.push(marker);
    });

    // Fit bounds if multiple dealers
    if (markers.length > 1) {
      const group = L.featureGroup(markers);
      map.fitBounds(group.getBounds().pad(0.1));
    }

    // Fade in map
    mapContainer.style.opacity = '0';
    mapContainer.style.transition = 'opacity 0.5s ease';
    setTimeout(() => {
      mapContainer.style.opacity = '1';
    }, 100);
  }

  /**
   * Create popup HTML content
   */
  function createPopupContent(dealer) {
    const address = `${dealer.street}<br>${dealer.zip} ${dealer.city}<br>${dealer.country}`;
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${dealer.lat},${dealer.lng}`;

    return `
      <div class="dealer-popup">
        <h4 class="dealer-popup__name">${escapeHtml(dealer.name)}</h4>
        <p class="dealer-popup__address">${address}</p>
        <a href="${mapsUrl}" target="_blank" rel="noopener" class="dealer-popup__route-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polygon points="3 11 22 2 13 21 11 13 3 11"/>
          </svg>
          Route planen
        </a>
      </div>
    `;
  }

  /**
   * Set active marker with visual feedback
   */
  function setActiveMarker(marker) {
    // Reset previous active marker
    if (activeMarker) {
      const prevEl = activeMarker.getElement();
      if (prevEl) {
        const img = prevEl.querySelector('.dealer-marker');
        if (img) {
          img.classList.remove('active');
          img.style.transform = '';
        }
      }
    }

    activeMarker = marker;

    // Set new active marker
    const el = marker.getElement();
    if (el) {
      const img = el.querySelector('.dealer-marker');
      if (img) {
        img.classList.add('active');
        img.style.transform = 'scale(1.2)';
      }
    }
  }

  /**
   * Initialize search functionality
   */
  function initSearch() {
    const searchInput = document.getElementById('dealer-search');
    const clearBtn = document.getElementById('search-clear');
    const resultsCount = document.getElementById('results-count');
    
    if (!searchInput) return;

    let debounceTimer;

    searchInput.addEventListener('input', (e) => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        filterDealers(e.target.value.toLowerCase().trim());
      }, 200);
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        searchInput.value = '';
        filterDealers('');
        searchInput.focus();
      });
    }
  }

  /**
   * Filter dealers based on search query
   */
  function filterDealers(query) {
    const cards = document.querySelectorAll('.dealer-card');
    const noResults = document.getElementById('no-results');
    const resultsCount = document.getElementById('results-count');
    let visibleCount = 0;

    cards.forEach(card => {
      const name = card.dataset.name || '';
      const city = card.dataset.city || '';
      const zip = card.dataset.zip || '';
      
      const matches = !query || 
        name.includes(query) || 
        city.includes(query) || 
        zip.includes(query);

      card.style.display = matches ? '' : 'none';
      if (matches) visibleCount++;
    });

    // Update results count
    if (resultsCount) {
      resultsCount.textContent = query 
        ? `${visibleCount} Händler gefunden` 
        : '';
    }

    // Show/hide no results message
    if (noResults) {
      noResults.style.display = visibleCount === 0 && query ? '' : 'none';
    }

    // Update map markers visibility
    markers.forEach(marker => {
      const card = document.querySelector(`[data-dealer-id="${marker.dealerId}"]`);
      if (card) {
        const isVisible = card.style.display !== 'none';
        if (isVisible) {
          marker.addTo(map);
        } else {
          map.removeLayer(marker);
        }
      }
    });

    // Fit visible markers
    const visibleMarkers = markers.filter(m => map.hasLayer(m));
    if (visibleMarkers.length > 0) {
      const group = L.featureGroup(visibleMarkers);
      map.fitBounds(group.getBounds().pad(0.1));
    }
  }

  /**
   * Initialize mobile toggle buttons
   */
  function initMobileToggle() {
    const toggleBtns = document.querySelectorAll('.toggle-btn');
    const sidebar = document.querySelector('.dealer-sidebar');
    const mapContainer = document.querySelector('.dealer-map-container');

    toggleBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const view = btn.dataset.view;

        toggleBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        if (view === 'list') {
          sidebar.classList.add('show');
          mapContainer.classList.add('hide');
        } else {
          sidebar.classList.remove('show');
          mapContainer.classList.remove('hide');
          // Invalidate map size after showing
          setTimeout(() => {
            if (map) map.invalidateSize();
          }, 100);
        }
      });
    });
  }

  /**
   * Initialize card click interactions
   */
  function initCardInteractions() {
    const cards = document.querySelectorAll('.dealer-card');

    cards.forEach(card => {
      card.addEventListener('click', (e) => {
        // Don't trigger if clicking on a link or button
        if (e.target.closest('a') || e.target.closest('button')) return;

        const lat = parseFloat(card.dataset.lat);
        const lng = parseFloat(card.dataset.lng);
        const dealerId = card.dataset.dealerId;

        if (map && lat && lng) {
          map.setView([lat, lng], 14, { animate: true });

          // Find and activate marker
          const marker = markers.find(m => m.dealerId === dealerId);
          if (marker) {
            setActiveMarker(marker);
            marker.openPopup();
          }
        }

        highlightCard(dealerId);

        // On mobile, switch to map view
        if (window.innerWidth <= 768) {
          const mapBtn = document.querySelector('.toggle-btn[data-view="map"]');
          if (mapBtn) mapBtn.click();
        }
      });
    });
  }

  /**
   * Highlight a dealer card
   */
  function highlightCard(dealerId) {
    // Remove previous highlight
    if (activeCard) {
      activeCard.classList.remove('active');
    }

    // Add new highlight
    const card = document.querySelector(`[data-dealer-id="${dealerId}"]`);
    if (card) {
      card.classList.add('active');
      activeCard = card;
    }
  }

  /**
   * Scroll to a dealer card in the list
   */
  function scrollToCard(dealerId) {
    const card = document.querySelector(`[data-dealer-id="${dealerId}"]`);
    const list = document.querySelector('.dealer-sidebar__list');
    
    if (card && list) {
      const cardTop = card.offsetTop - list.offsetTop;
      list.scrollTo({
        top: cardTop - 16,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Initialize route buttons
   */
  function initRouteButtons() {
    document.querySelectorAll('.dealer-card__route-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const lat = btn.dataset.lat;
        const lng = btn.dataset.lng;
        const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
        window.open(url, '_blank', 'noopener');
      });
    });
  }

  /**
   * Animate elements on scroll
   */
  function animateElements() {
    const elements = document.querySelectorAll('[data-animate]');
    
    if (prefersReducedMotion) {
      elements.forEach(el => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const delay = parseFloat(entry.target.dataset.animateDelay) || 0;
          setTimeout(() => {
            entry.target.classList.add('is-visible');
          }, delay * 1000);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    elements.forEach(el => observer.observe(el));
  }

  /**
   * Escape HTML to prevent XSS
   */
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
